import { createTheme } from '@mui/material/styles';

export const getTheme = (mode = 'light') => {
const isDark = mode === 'dark';
return createTheme({
  palette: {
    mode,
    primary: {
      main: '#1565c0',
      light: '#1976d2',
      dark: '#0d47a1',
      contrastText: '#fff',
    },
    secondary: {
      main: '#00796b',
      light: '#26a69a',
      dark: '#004d40',
      contrastText: '#fff',
    },
    background: {
      default: isDark ? '#0f1923' : '#f0f4f8',
      paper: isDark ? '#1a2535' : '#ffffff',
    },
    success: { main: '#2e7d32', light: isDark ? '#1b4d1f' : '#e8f5e9' },
    warning: { main: '#e65100', light: isDark ? '#4a2000' : '#fff3e0' },
    error: { main: '#c62828', light: isDark ? '#4a0e0e' : '#ffebee' },
    text: {
      primary: isDark ? '#e2e8f0' : '#1a2332',
      secondary: isDark ? '#8fa3b8' : '#556070',
    },
    divider: isDark ? '#2a3a4d' : '#e2e8f0',
    grey: {
      50:  isDark ? '#1e2d3d' : '#f8fafc',
      100: isDark ? '#243040' : '#f1f5f9',
      200: isDark ? '#2a3a4d' : '#e2e8f0',
    },
  },

  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: { fontWeight: 700, fontSize: '1.6rem', letterSpacing: '-0.01em' },
    h5: { fontWeight: 700, fontSize: '1.25rem' },
    h6: { fontWeight: 600, fontSize: '1rem' },
    subtitle1: { fontWeight: 500, fontSize: '0.95rem' },
    subtitle2: { fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#8fa3b8' : '#556070' },
    body1: { fontSize: '0.9rem' },
    body2: { fontSize: '0.82rem' },
    caption: { fontSize: '0.75rem' },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
  },

  shape: { borderRadius: 8 },

  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.07)',
    '0 1px 4px rgba(0,0,0,0.08)',
    '0 2px 8px rgba(0,0,0,0.1)',
    '0 4px 16px rgba(0,0,0,0.1)',
    ...Array(20).fill('0 4px 24px rgba(0,0,0,0.12)'),
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: isDark ? '#0f1923' : '#f0f4f8' },
        '*': { boxSizing: 'border-box' },
        '::-webkit-scrollbar': { width: 6 },
        '::-webkit-scrollbar-track': { background: 'transparent' },
        '::-webkit-scrollbar-thumb': { background: isDark ? '#3a4f66' : '#cbd5e0', borderRadius: 3 },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: isDark ? '#0a1628' : '#0d47a1',
          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '6px 16px',
          fontWeight: 600,
          '&.MuiButton-containedPrimary': {
            boxShadow: '0 1px 3px rgba(21,101,192,0.3)',
            '&:hover': { boxShadow: '0 2px 6px rgba(21,101,192,0.4)' },
          },
        },
        sizeSmall: { padding: '4px 10px', fontSize: '0.8rem' },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}`,
          boxShadow: 'none',
        },
        elevation1: { boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}` },
        elevation2: { boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}` },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}`,
          boxShadow: 'none',
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}`,
          borderRadius: 8,
          overflow: 'hidden',
        },
      },
    },

    MuiTable: {
      styleOverrides: { root: { borderCollapse: 'collapse' } },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: isDark ? '#1e2d3d' : '#f8fafc',
            fontWeight: 700,
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: isDark ? '#8fa3b8' : '#556070',
            borderBottom: `2px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}`,
            padding: '10px 16px',
            whiteSpace: 'nowrap',
          },
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:hover': { backgroundColor: isDark ? '#1e2d3d' : '#f8fafc' },
            '&:last-child td': { borderBottom: 'none' },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}`,
          padding: '10px 16px',
          fontSize: '0.875rem',
        },
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}`,
          backgroundColor: isDark ? '#1e2d3d' : '#f8fafc',
          fontSize: '0.82rem',
        },
        selectLabel: { fontSize: '0.82rem' },
        displayedRows: { fontSize: '0.82rem' },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: isDark ? '#1a2535' : '#fff',
            '& fieldset': { borderColor: isDark ? '#2a3a4d' : '#dde3ec' },
            '&:hover fieldset': { borderColor: '#1976d2' },
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: { backgroundColor: isDark ? '#1a2535' : '#fff' },
      },
    },

    MuiFormControl: {
      defaultProps: { size: 'small' },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.72rem' },
        sizeSmall: { height: 22 },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: 'none',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '1.1rem',
          padding: '20px 24px 12px',
          borderBottom: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}`,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingTop: '20px !important',  // override MUI's adjacent-sibling rule that zeroes paddingTop
          paddingBottom: '20px',
          paddingLeft: '24px',
          paddingRight: '24px',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          borderTop: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}`,
          gap: 8,
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          border: `1px solid ${isDark ? '#2a3a4d' : '#e2e8f0'}`,
          borderRadius: '8px !important',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '& + &': { marginTop: 8 },
          '&.Mui-expanded': { margin: '0 0 8px 0' },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 48,
          '&.Mui-expanded': { minHeight: 48 },
        },
        content: { margin: '12px 0', '&.Mui-expanded': { margin: '12px 0' } },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 6, fontSize: '0.85rem' },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: '3px 3px 0 0' },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'none',
          minWidth: 'auto',
          padding: '10px 20px',
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: isDark ? '#2a3a4d' : '#e2e8f0' },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          backgroundColor: isDark ? '#243040' : '#1a2332',
          borderRadius: 4,
        },
      },
    },
  },
});
};

export default getTheme('light');
